import { AnyAction, Dispatch, Store } from 'redux';

/*
 * Minimal in-house replacement for the archived react-router-redux package.
 * Action types, action shapes and the resulting store state are identical to
 * react-router-redux@4, so reducers, middleware and sagas built on
 * `@@router/*` actions keep working unchanged. Routing itself is owned by the
 * react-router history singleton; the store is only an observer.
 */

export const LOCATION_CHANGE = '@@router/LOCATION_CHANGE';
export const CALL_HISTORY_METHOD = '@@router/CALL_HISTORY_METHOD';

export type LocationDescriptor =
  | string
  | {
      pathname?: string;
      search?: string;
      query?: Record<string, unknown>;
      hash?: string;
      state?: unknown;
    };

export type RouterLocation = {
  pathname: string;
  search: string;
  query: Record<string, string | undefined>;
  hash: string;
  state?: unknown;
  action: string;
  key?: string;
};

type NavigationMethod = 'push' | 'replace';

type CallHistoryMethodAction = {
  type: typeof CALL_HISTORY_METHOD;
  payload: { method: NavigationMethod; args: [LocationDescriptor] };
};

type RouterHistory = {
  push: (location: LocationDescriptor) => void;
  replace: (location: LocationDescriptor) => void;
  getCurrentLocation: () => RouterLocation;
  listen: (listener: (location: RouterLocation) => void) => () => void;
};

const updateLocation =
  (method: NavigationMethod) =>
  (location: LocationDescriptor): CallHistoryMethodAction => ({
    type: CALL_HISTORY_METHOD,
    payload: { method, args: [location] },
  });

export const push = updateLocation('push');
export const replace = updateLocation('replace');

const isCallHistoryMethodAction = (
  action: AnyAction
): action is CallHistoryMethodAction => action.type === CALL_HISTORY_METHOD;

export const routerMiddleware =
  (history: RouterHistory) => () => (next: Dispatch) => (action: AnyAction) => {
    if (!isCallHistoryMethodAction(action)) {
      return next(action);
    }

    const { method, args } = action.payload;
    history[method](...args);
    return undefined;
  };

export const dispatchLocationChangesToStore = (
  history: RouterHistory,
  store: Store
) => {
  const dispatchLocationChange = (location: RouterLocation) =>
    store.dispatch({ type: LOCATION_CHANGE, payload: location });

  // history v3's `listen` does not fire for the current location, so the
  // initial one must be dispatched explicitly (react-router-redux did the same)
  dispatchLocationChange(history.getCurrentLocation());
  history.listen(dispatchLocationChange);
};
